// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import type { Translation } from "@/translations/types";
import type { TyLang, TyLangValue } from "@/types";
// --- Logic --------------------------------------------------------------------------------------
export { resolvePreviewKind, type TyPreviewKind } from "./preview";
export { pressToOpen, rightClickToOpen } from "./cards";
export { stageTone, leadTitle, dueState, nextDue, type TyDueState } from "./crm";
export { canManageRole } from "./roles";
export { makeThumbnail } from "./thumbnail";
export { warrantyState, warrantyDaysLeft, warrantyElapsed } from "./warranties";

/**
 * Reads a tenant-authored bilingual field. A company may publish in one language only, so the asked-for
 * entry is a preference and the first one that exists is the answer.
 */
export function readLangValue(values: TyLangValue[] | undefined, lang: TyLang): string {
    if (!values?.length) return "";
    return values.find((entry) => entry.lang === lang)?.value ?? values[0].value;
}

/**
 * Bytes as a person would say them. Fixed to one decimal from MB up, because "1.4 GB" is the number
 * someone decides on and "1468006400 bytes" is not.
 */
export function formatBytes(bytes: number, lang: TyLang = "ar"): string {
    const units = lang === "ar" ? ["بايت", "ك.ب", "م.ب", "ج.ب", "ت.ب"] : ["B", "KB", "MB", "GB", "TB"];
    if (!bytes) return `0 ${units[1]}`;

    let step = Math.floor(Math.log(bytes) / Math.log(1024));
    step = Math.min(step, units.length - 1);
    const value = bytes / 1024 ** step;

    return `${step >= 2 ? value.toFixed(1) : Math.round(value)} ${units[step]}`;
}

/**
 * Whether a nav link points at the section the person is already in. Such a link **replaces** rather
 * than pushes: a screen that keeps its place in the URL — the drive and its folders — would otherwise
 * stack an entry every time someone taps its nav button to get back to its top, and the back button
 * would walk them straight back in.
 */
export function isSectionReentry(path: string, pathname: string): boolean {
    return path === pathname;
}

/**
 * Riyals as a person would say them: grouped, never rounded, decimals only when there are any — a
 * price is "1,500,000" out loud, not "1500000.00".
 *
 * **Always Western digits, in both languages.** Arabic-Indic numerals are correct Arabic and wrong
 * here: prices are read off contracts, bank transfers and spreadsheets that all use Western digits,
 * and a figure a person cannot copy straight across is a figure they have to retype.
 */
export function formatMoney(value: number): string {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value);
}

/**
 * How a rent price is said: the period it covers is a **unit of measure** on the figure, not a
 * sentence after it. "95,000 ريال/سنة" is a rate; "95,000 ر.س / كل 12 أشهر" is a calculation.
 *
 * The named periods are the ones a person has a word for. Anything else falls back to counting
 * months, because "every 7 months" has no name and pretending otherwise would be worse.
 */
export function rentUnitKey(months?: number | null): "month" | "quarter" | "half" | "year" | null {
    if (months === 1) return "month";
    if (months === 3) return "quarter";
    if (months === 6) return "half";
    if (months === 12) return "year";
    return null;
}

type TyPriced = { price?: number | null; offer?: string | null; rentPeriod?: number | null };

/** The whole label, so the card and the screen cannot drift into saying it two different ways. */
export function formatPrice(record: TyPriced, t: Translation): string {
    if (!record.price) return t.properties.notSet;

    const amount = `${formatMoney(record.price)} ${t.properties.currency}`;
    if (record.offer !== "rent") return amount;

    const key = rentUnitKey(record.rentPeriod);
    if (key) return `${amount}/${t.properties.rentUnits[key]}`;
    if (record.rentPeriod) return `${amount}/${record.rentPeriod} ${t.properties.rentUnits.months}`;
    return amount;
}

/**
 * A date as a person reads it. The API sends ISO strings; nobody wants to read one.
 *
 * Arabic month names, **Western digits** — the same call `formatMoney` makes, and for the same
 * reason: these numbers are read alongside contract dates, bank records and the rest of the app's
 * figures. `ar-SA-u-nu-latn` is the Arabic calendar and wording with Latin numerals.
 *
 * **Pinned to Riyadh, like the picker that wrote it.** Without the zone this renders in whatever
 * the laptop is set to, and a date stored as midnight in Riyadh comes back as the day before for
 * anybody west of it — which is the same bug `DateTimeInput` pins `Settings.defaultZone` to avoid.
 * Reading and writing have to agree on the zone or a signing date moves by a day on half the
 * machines that open it.
 */
export function formatDate(value: string | null | undefined, lang: TyLang = "ar"): string {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat(lang === "ar" ? "ar-SA-u-nu-latn-ca-gregory" : "en-GB", {
        timeZone: constants.timeZone,
        year: "numeric",
        month: "short",
        day: "numeric",
    }).format(date);
}

/**
 * A `YYYY-MM` bucket as a chart axis label — the short month name, and the year only when the series
 * crosses one, because "Jan" following "Dec" with no year is the one place a six-month axis misleads.
 */
export function formatMonth(key: string, lang: TyLang = "ar"): string {
    const [year, month] = key.split("-").map(Number);
    if (!year || !month) return key;

    const date = new Date(Date.UTC(year, month - 1, 1));
    const label = new Intl.DateTimeFormat(lang === "ar" ? "ar-SA-u-nu-latn-ca-gregory" : "en-GB", { timeZone: "UTC", month: "short" }).format(date);

    return month === 1 ? `${label} ${year}` : label;
}

/** A date **and** a time, for a history entry — "yesterday" is not precise enough to settle a dispute. */
export function formatDateTime(value: string | null | undefined, lang: TyLang = "ar"): string {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat(lang === "ar" ? "ar-SA-u-nu-latn-ca-gregory" : "en-GB", {
        timeZone: constants.timeZone,
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date);
}

/**
 * Where this organization's public catalog lives.
 *
 * Built from the API's own address rather than a second environment variable: they are the same
 * server, and two settings that must agree are one setting somebody will eventually get wrong. In
 * production nginx answers the catalog on `{slug}.lozicore.com`, which the deploy maps onto this
 * same path — so the link is right in both places without the app knowing which it is in.
 */
export const catalogUrl = (slug: string): string => `${String(constants.url).replace(/\/clients\/?$/, "")}/catalog/${encodeURIComponent(slug)}`;

/**
 * The public address of one project, or of one unit inside it.
 *
 * **The shape of a catalog link is decided in one place**, and this is it on this side: a project is
 * its slug and a unit is its number, with nothing in between. A record that is not published has no
 * public address at all, which is why every caller checks `isPublic` before offering to share one.
 */
export const catalogRecordUrl = (organizationSlug: string, projectSlug: string, unitNumber?: string | null): string => {
    const base = `${catalogUrl(organizationSlug)}/${encodeURIComponent(projectSlug)}`;
    return unitNumber ? `${base}/${encodeURIComponent(unitNumber)}` : base;
};

/**
 * Text onto the clipboard, anywhere.
 *
 * `navigator.clipboard` exists only in a secure context — over `https` or on `localhost`. Opening
 * the dev server on a phone through the LAN address is neither, so it is missing exactly when it is
 * most wanted. The textarea fallback is the old way, and it works everywhere.
 */
export async function copyText(value: string): Promise<boolean> {
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(value);
            return true;
        }

        const field = document.createElement("textarea");
        field.value = value;
        field.setAttribute("readonly", "");
        field.style.position = "fixed";
        field.style.opacity = "0";
        document.body.appendChild(field);
        field.select();
        document.execCommand("copy");
        document.body.removeChild(field);
        return true;
    } catch {
        return false;
    }
}
